import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { PlainButton, useIsMobile } from 'components/v3';
import { InfoTip } from '../../help';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';
import { NameInput } from './ColumnsModal';
import DialogShell, {
  DialogButton,
  Foot,
  FootNote,
  Label,
  LinkButton,
  Spacer,
} from './DialogShell';
import FieldChooser from './FieldChooser';

// Export submissions (design sub_modals.jsx ExportModal): the format, which
// entries (when a search is on), and which fields in what order — start
// from a saved view, every field, or the last export. The last choice is
// remembered per form. The page has the server build the file and
// downloads it; the button reads "Preparing…" until then.

export const FORMATS = [
  {
    id: 'csv',
    icon: 'file-text',
    label: 'CSV',
    sub: 'Any spreadsheet app',
    cta: 'Download CSV',
  },
  {
    id: 'xlsx',
    icon: 'sheet',
    label: 'Excel',
    sub: '.xlsx workbook',
    cta: 'Download Excel',
  },
  {
    id: 'pdf',
    icon: 'download',
    label: 'PDF',
    sub: 'Formatted report',
    cta: 'Download PDF',
  },
];

const Options = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$mobile ? '12px' : '16px')};
  padding: ${(p) => (p.$mobile ? '0 16px 14px' : '0 24px 20px')};
`;

const Option = styled.div`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$mobile ? 'minmax(0, 1fr)' : '110px minmax(0, 1fr)'};
  align-items: center;
  gap: ${(p) => (p.$mobile ? '8px' : '16px')};
`;

const Formats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${(p) => (p.$mobile ? '8px' : '10px')};
`;

const Format = styled(PlainButton)`
  display: flex;
  flex-direction: ${(p) => (p.$mobile ? 'column' : 'row')};
  align-items: ${(p) => (p.$mobile ? 'flex-start' : 'center')};
  gap: ${(p) => (p.$mobile ? '8px' : '12px')};
  min-width: 0;
  padding: ${(p) => (p.$mobile ? '10px' : '10px 14px 10px 10px')};
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$on ? ORANGE : sx(p.theme).line2)};
  background: ${(p) => (p.$on ? sx(p.theme).tint : sx(p.theme).inset)};
  box-shadow: ${(p) => (p.$on ? '0 0 0 3px rgba(250, 70, 22, 0.12)' : 'none')};
  text-align: left;

  &:hover {
    border-color: ${(p) => (p.$on ? ORANGE : sx(p.theme).tintB)};
  }
`;

const FormatIcon = styled.span`
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$on ? ORANGE : sx(p.theme).fill)};
  color: ${(p) => (p.$on ? '#FFFFFF' : sx(p.theme).text2)};
`;

const FormatText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 15px;
    font-weight: 700;
    color: ${(p) => sx(p.theme).text};
  }

  span {
    font-size: 12.5px;
    color: ${(p) => sx(p.theme).label};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Segmented = styled.div`
  display: ${(p) => (p.$mobile ? 'flex' : 'inline-flex')};
  justify-self: ${(p) => (p.$mobile ? 'stretch' : 'start')};
  gap: 2px;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
`;

const Segment = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 ${(p) => (p.$mobile ? '8px' : '14px')};
  ${(p) => (p.$mobile ? 'flex: 1;' : '')}
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => (p.$on ? sx(p.theme).fill : 'transparent')};
  color: ${(p) => (p.$on ? sx(p.theme).text : sx(p.theme).label)};
`;

const Num = styled.span`
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: ${(p) => (p.$on ? 'rgba(250, 70, 22, 0.2)' : sx(p.theme).fill)};
  color: ${(p) => (p.$on ? sx(p.theme).otext : sx(p.theme).label)};
`;

const Presets = styled.div`
  display: flex;
  flex-wrap: ${(p) => (p.$mobile ? 'nowrap' : 'wrap')};
  gap: 8px;
  ${(p) =>
    p.$mobile
      ? 'overflow-x: auto; scrollbar-width: none; margin: 0 -16px; padding: 0 16px;'
      : ''}
`;

const Preset = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: none;
  height: 34px;
  padding: 0 6px 0 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$on ? ORANGE : sx(p.theme).line2)};
  background: ${(p) => (p.$on ? sx(p.theme).tint : 'transparent')};
  color: ${(p) => (p.$on ? sx(p.theme).otext : sx(p.theme).text2)};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: ${(p) => (p.$static ? 'default' : 'pointer')};

  &:hover {
    border-color: ${(p) => (p.$on ? ORANGE : sx(p.theme).tintB)};
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const same = (a, b) => a.length === b.length && a.every((k, i) => k === b[i]);

const ExportModal = ({
  catalog,
  keys,
  views,
  activeId,
  formName,
  lastChoice,
  searching,
  matchingCount,
  allCount,
  onRun,
  onSaveView,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const lastCols =
    lastChoice && Array.isArray(lastChoice.cols)
      ? lastChoice.cols.filter((k) => keys.includes(k))
      : [];
  const active = views.find((v) => v.id === activeId) || views[0];
  const [fmt, setFmt] = useState(
    lastChoice && FORMATS.some((f) => f.id === lastChoice.fmt)
      ? lastChoice.fmt
      : 'csv'
  );
  const [cols, setCols] = useState(
    lastCols.length ? lastCols : active.cols.filter((k) => keys.includes(k))
  );
  const [scope, setScope] = useState(searching ? 'matching' : 'all');
  const [naming, setNaming] = useState(null);
  const [running, setRunning] = useState(false);
  // The run can outlast the dialog (Esc while it prepares): the page still
  // downloads the file, but this dialog must not touch its state or close
  // a dialog opened since.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const presets = views
    .map((v) => ({
      id: v.id,
      label: v.name,
      icon: v.builtIn ? 'table' : 'bookmark',
      cols: v.cols.filter((k) => keys.includes(k)),
    }))
    .concat([{ id: '_all', label: 'All fields', icon: 'layers', cols: keys }]);
  if (lastCols.length && !presets.some((p) => same(p.cols, lastCols))) {
    presets.push({
      id: '_last',
      label: 'Last export',
      icon: 'clock',
      cols: lastCols,
    });
  }
  const match = presets.find((p) => same(p.cols, cols));
  const rowCount = scope === 'matching' ? matchingCount : allCount;
  const format = FORMATS.find((f) => f.id === fmt) || FORMATS[0];
  const canSaveView =
    cols.length > 0 && (!match || match.id === '_all' || match.id === '_last');

  const run = async () => {
    if (!cols.length || !rowCount || running) return;
    setRunning(true);
    const done = await onRun({ fmt, cols, scope });
    if (!mounted.current) return;
    setRunning(false);
    if (done !== false) onClose();
  };

  const saveView = () => {
    const name = (naming || '').trim();
    if (!name) return;
    onSaveView(name, cols.slice());
    setNaming(null);
  };

  const close = () => {
    if (naming != null) setNaming(null);
    else onClose();
  };

  return (
    <DialogShell
      label="Export submissions"
      icon="download"
      title="Export submissions"
      sub={formName}
      onClose={close}
    >
      <Options $mobile={isMobile}>
        <Option $mobile={isMobile}>
          <Label>Format</Label>
          <Formats
            $mobile={isMobile}
            role="group"
            aria-label="Format"
            data-tour="export-format"
          >
            {FORMATS.map((f) => (
              <Format
                key={f.id}
                $on={fmt === f.id}
                $mobile={isMobile}
                aria-pressed={fmt === f.id}
                onClick={() => setFmt(f.id)}
              >
                <FormatIcon $on={fmt === f.id}>
                  <Glyph name={f.icon} size={18} />
                </FormatIcon>
                <FormatText>
                  <strong>{f.label}</strong>
                  {isMobile ? null : <span>{f.sub}</span>}
                </FormatText>
              </Format>
            ))}
          </Formats>
        </Option>
        {searching ? (
          <Option $mobile={isMobile}>
            <Label>Entries</Label>
            <Segmented $mobile={isMobile} role="group" aria-label="Entries">
              <Segment
                $mobile={isMobile}
                $on={scope === 'matching'}
                aria-pressed={scope === 'matching'}
                onClick={() => setScope('matching')}
              >
                Matching your search
                <Num>{matchingCount.toLocaleString()}</Num>
              </Segment>
              <Segment
                $mobile={isMobile}
                $on={scope === 'all'}
                aria-pressed={scope === 'all'}
                onClick={() => setScope('all')}
              >
                All entries
                <Num>{allCount.toLocaleString()}</Num>
              </Segment>
            </Segmented>
          </Option>
        ) : null}
        <Option $mobile={isMobile}>
          <Label>
            Fields
            <InfoTip
              title="Fields in the file"
              text="Check fields on the left. The list on the right is the file’s column order, left to right. Start from a saved view to save time."
            />
          </Label>
          <Presets $mobile={isMobile} data-tour="export-presets">
            {presets.map((p) => (
              <Preset
                key={p.id}
                $on={!!match && match.id === p.id}
                aria-pressed={!!match && match.id === p.id}
                data-help="export-preset"
                onClick={() => setCols(p.cols.slice())}
              >
                <Glyph name={p.icon} size={14} />
                {p.label}
                <Num $on={!!match && match.id === p.id}>{p.cols.length}</Num>
              </Preset>
            ))}
            {!match ? (
              <Preset as="span" $on $static>
                <Glyph name="sliders-horizontal" size={14} />
                Custom
                <Num $on>{cols.length}</Num>
              </Preset>
            ) : null}
          </Presets>
        </Option>
      </Options>
      <FieldChooser
        catalog={catalog}
        value={cols}
        onChange={setCols}
        selTitle="In the file"
        emptyText="Pick the fields to include in the file."
        tour="export-fields"
      />
      <Foot $mobile={isMobile}>
        {naming != null ? (
          <NameRow>
            <NameInput
              $sm
              autoFocus
              aria-label="Name this view"
              placeholder="Name this view"
              maxLength={40}
              value={naming}
              onChange={(e) => setNaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveView();
              }}
              style={{ flex: '0 1 240px' }}
            />
            <DialogButton $sm disabled={!naming.trim()} onClick={saveView}>
              Save
            </DialogButton>
            <DialogButton $sm onClick={() => setNaming(null)}>
              Cancel
            </DialogButton>
          </NameRow>
        ) : null}
        {naming == null && canSaveView ? (
          <LinkButton onClick={() => setNaming('')}>
            <Glyph name="bookmark" size={14} />
            Save as table view
          </LinkButton>
        ) : null}
        {isMobile ? null : <Spacer />}
        {isMobile ? null : (
          <FootNote>{`${rowCount.toLocaleString()} rows × ${
            cols.length
          } columns`}</FootNote>
        )}
        {isMobile ? null : (
          <DialogButton onClick={onClose}>Cancel</DialogButton>
        )}
        <DialogButton
          $primary
          $grow={isMobile}
          data-tour="export-download"
          disabled={!cols.length || !rowCount || running}
          onClick={run}
        >
          <Glyph name="download" size={16} />
          {running ? 'Preparing…' : format.cta}
        </DialogButton>
      </Foot>
    </DialogShell>
  );
};

ExportModal.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object).isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  views: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      cols: PropTypes.arrayOf(PropTypes.string).isRequired,
      builtIn: PropTypes.bool,
    })
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  formName: PropTypes.string,
  lastChoice: PropTypes.shape({
    fmt: PropTypes.string,
    cols: PropTypes.arrayOf(PropTypes.string),
  }),
  searching: PropTypes.bool,
  matchingCount: PropTypes.number,
  allCount: PropTypes.number,
  // ({ fmt, cols, scope }) → Promise; resolving `false` keeps the dialog open.
  onRun: PropTypes.func.isRequired,
  onSaveView: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ExportModal.defaultProps = {
  formName: '',
  lastChoice: null,
  searching: false,
  matchingCount: 0,
  allCount: 0,
};

export default ExportModal;
